import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useDealHeartbeatPreview } from 'api/aws/heartbeat/useDealHeartbeatPreview'
import type { DealHeartbeatPreview } from 'api/aws/heartbeat/useDealHeartbeatPreview'

/**
 * Confirmation modal shown before a no-signature deal's "Create Contract"
 * action fires. Outlines the two side-effects:
 *
 *   1. A PandaDoc contract is created from the no-signature template (no
 *      signing recipients, no auth flow).
 *   2. The deal is immediately pushed to Heartbeat — same orchestrator
 *      as the dev push button and the PandaDoc webhook Deal Won path.
 *
 * The Heartbeat push section is dynamic — fetches the live preview so
 * it can label the push correctly (Flow A new contract / Flow A
 * renewal / Flow B service change / Flow IO insertion order) and warn
 * up-front if the org isn't linked to a Heartbeat account (in which
 * case the push will skip cleanly with no writes).
 */

interface NoSignatureCreateConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  dealPK: string | undefined
  dealSK: string | undefined
  isCreating: boolean
}

const NoSignatureCreateConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  dealPK,
  dealSK,
  isCreating,
}: NoSignatureCreateConfirmDialogProps) => {
  const { data, isLoading, isError } = useDealHeartbeatPreview(open ? dealPK : undefined, open ? dealSK : undefined)
  const preview = data?.preview

  return (
    <Dialog open={open} onClose={isCreating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create no-signature contract</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              1. PandaDoc
            </Typography>
            <DialogContentText sx={{ fontSize: 14 }}>
              A contract will be created in PandaDoc from the no-signature template. It will land as
              <strong> Completed Internally</strong> — no signer recipients, no signing flow.
            </DialogContentText>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              2. Heartbeat
            </Typography>
            <HeartbeatSection isLoading={isLoading} isError={isError} preview={preview} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isCreating} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={isCreating || isLoading} variant="contained" color="secondary">
          {isCreating ? 'Creating…' : 'Create Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface HeartbeatSectionProps {
  isLoading: boolean
  isError: boolean
  preview: DealHeartbeatPreview | undefined
}

const HeartbeatSection = ({ isLoading, isError, preview }: HeartbeatSectionProps) => {
  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary">
          Determining push type…
        </Typography>
      </Stack>
    )
  }

  if (isError || !preview) {
    return (
      <Alert severity="warning" sx={{ fontSize: 13 }}>
        Couldn't load the Heartbeat preview. The contract will still be created, but the Heartbeat push may not fire
        correctly — check the Heartbeat Preview tab before confirming.
      </Alert>
    )
  }

  // Account-link warnings come first — these block the push entirely.
  if (preview.skipReason === 'org_not_found' || preview.skipReason === 'deal_not_found') {
    return (
      <Alert severity="error" sx={{ fontSize: 13 }}>
        Heartbeat preview can't resolve this {preview.skipReason === 'org_not_found' ? 'organization' : 'deal'}. The
        push will be skipped.
      </Alert>
    )
  }

  const accountLinked = preview.account?.action === 'already_linked'
  if (!accountLinked) {
    return (
      <Alert severity="warning" sx={{ fontSize: 13 }}>
        This organization is <strong>not linked</strong> to a Heartbeat account ({preview.org?.name ?? '?'}). The
        contract will still be created in PandaDoc, but the Heartbeat push will be <strong>skipped</strong> with
        <code> org_not_linked</code>. Resolve the link before retrying the push.
      </Alert>
    )
  }

  const { flow, flow_a, flow_b, flow_io, renewal_context } = preview

  if (flow === 'A' && flow_a) {
    const isRenewal = !!renewal_context
    const subCount = flow_a.subscriptions.length
    return (
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            size="small"
            color="primary"
            label={isRenewal ? 'Flow A · Renewal Contract' : 'Flow A · New Contract'}
          />
          <Typography variant="body2" color="text.secondary">
            {isRenewal
              ? `Renewal of v${renewal_context.predecessor.semanticVersion ?? '?'}`
              : 'No predecessor on file'}
          </Typography>
        </Stack>
        <DialogContentText sx={{ fontSize: 14 }}>
          Pushes a new contract <strong>{subCount}</strong> subscription(s) to Heartbeat, starting{' '}
          {flow_a.start_date}
          {flow_a.end_date ? ` through ${flow_a.end_date}` : ''}.
          {flow_a.is_flighted ? (
            <>
              {' '}
              <strong>{flow_a.flighted_activations.length}</strong> scheduled MRR change(s) will be
              queued as separate service-change submissions for their effective dates.
            </>
          ) : null}
          {isRenewal && renewal_context.reason === 'no_hb_contract' ? (
            <>
              {' '}
              Heartbeat has no contract for this account yet, so v
              {renewal_context.predecessor.semanticVersion ?? '?'} is backfilled as history first and
              each change is measured against it.
            </>
          ) : isRenewal ? (
            <>
              {' '}
              The predecessor's existing subscriptions will expire naturally on{' '}
              {renewal_context.predecessor.endDate ?? '?'}.
            </>
          ) : null}
        </DialogContentText>
      </Stack>
    )
  }

  if (flow === 'B' && flow_b) {
    const changeCount = flow_b.changes.length
    return (
      <Stack spacing={1}>
        <Chip size="small" color="secondary" label="Flow B · Service Change" />
        <DialogContentText sx={{ fontSize: 14 }}>
          {changeCount > 0 ? (
            <>
              Pushes <strong>{changeCount}</strong> service-change submission(s) against the predecessor (
              <code>{flow_b.predecessor.SK}</code>).
            </>
          ) : (
            <>
              No per-service MRR change vs. the predecessor — Heartbeat push will land with{' '}
              <strong>0 submissions</strong>{' '}
              {flow_b.composition_warnings.length > 0 ? (
                <>
                  but {flow_b.composition_warnings.length} service line(s) have product composition changes that may
                  need manual reconciliation.
                </>
              ) : null}
            </>
          )}
        </DialogContentText>
      </Stack>
    )
  }

  if (flow === 'IO' && flow_io) {
    return (
      <Stack spacing={1}>
        <Chip size="small" color="info" label="Flow IO · Insertion Order" />
        <DialogContentText sx={{ fontSize: 14 }}>
          Pushes <strong>{flow_io.submissions.length}</strong> temporary Increase submission(s) with{' '}
          <code>ongoing_request=No</code> — one per product, layered on top of the existing MSA.
          {flow_io.baseline_missing ? (
            <>
              {' '}
              <em>Baseline MRR couldn't be resolved — current_mrr will land at 0 across the board. Verify before
              proceeding.</em>
            </>
          ) : null}
        </DialogContentText>
      </Stack>
    )
  }

  return (
    <Alert severity="info" sx={{ fontSize: 13 }}>
      No Heartbeat push will fire for this deal (flow detected: <code>{flow}</code>).
    </Alert>
  )
}

export default NoSignatureCreateConfirmDialog
